// A Sentinel's brands and their cap in each, inside their expanded row on the Users page.
// Ticking a brand puts them on it; the cap, which is optional, is the most open tickets they may
// hold there. Left empty, they follow the brand's cap for every Sentinel (Sentinel workspace
// settings), and keep following it when it changes; with no brand cap either, there is no cap.
// A number here is their own cap and wins over the brand's.
// Administrators only.

import { useEffect, useMemo, useState } from 'react';
import { Building2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui/sonner';
import { invalidateQueries } from '@/lib/query-client';
import type { SentinelUserBrandDto } from '@/modules/sentinel-workspace/types';
import { sentinelWorkspaceService } from '@/services/sentinelWorkspace.service';

interface SentinelBrandsSectionProps {
	userId: number;
	name: string;
	/** What is saved now. */
	saved: SentinelUserBrandDto[];
	/** The account's active brands. */
	brands: { id: string; name: string }[];
}

/** Brand id -> cap, for the brands ticked. */
type Draft = Record<string, number | null>;

const draftOf = (rows: SentinelUserBrandDto[]): Draft =>
	Object.fromEntries(rows.map((row) => [String(row.buId), row.maxActiveTickets]));

const sameDraft = (a: Draft, b: Draft): boolean =>
	Object.keys(a).length === Object.keys(b).length && Object.entries(a).every(([id, cap]) => id in b && b[id] === cap);

export function SentinelBrandsSection({ userId, name, saved, brands }: SentinelBrandsSectionProps) {
	const savedDraft = useMemo(() => draftOf(saved), [saved]);
	const [draft, setDraft] = useState<Draft>(savedDraft);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => setDraft(savedDraft), [savedDraft]);

	const canSave = !sameDraft(draft, savedDraft) && !isSaving;

	const toggle = (brandId: string, works: boolean) =>
		setDraft((previous) => {
			const next = { ...previous };
			// Every Sentinel follows the brand's cap (Sentinel workspace settings).
			if (works) next[brandId] = null;
			else delete next[brandId];
			return next;
		});

	const handleSave = async () => {
		setIsSaving(true);
		try {
			await sentinelWorkspaceService.saveUserBrands(
				userId,
				Object.entries(draft).map(([buId, maxActiveTickets]) => ({ buId: Number(buId), maxActiveTickets })),
			);
			void invalidateQueries(['sentinel', 'user-brands']);
			toast.success(`Saved ${name}'s brands`);
		} catch {
			toast.error(`Failed to save ${name}'s brands. Please try again.`);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div className='bg-muted/30 border-t px-4 py-3 space-y-3'>
			<div className='flex items-center gap-1.5 text-xs font-medium text-muted-foreground'>
				<Building2 className='h-3 w-3' />
				Brands
				<span className='text-muted-foreground/70'>(the brands this Sentinel works, and an optional cap on open tickets in each)</span>
			</div>

			{brands.length === 0 ? (
				<p className='text-xs text-muted-foreground'>This account has no active brands.</p>
			) : (
				<div className='flex flex-col gap-2'>
					{brands.map((brand) => {
						const works = brand.id in draft;
						return (
							<div key={brand.id} className='flex items-center gap-3'>
								<label className='flex w-48 items-center gap-1.5 text-xs text-foreground'>
									<Checkbox
										aria-label={`${name} works ${brand.name}`}
										checked={works}
										disabled={isSaving}
										onCheckedChange={(checked) => toggle(brand.id, checked === true)}
									/>
									<span className='truncate'>{brand.name}</span>
								</label>
							</div>
						);
					})}
				</div>
			)}

			<button
				type='button'
				onClick={() => void handleSave()}
				disabled={!canSave}
				className={[
					'h-7 rounded-full bg-ink-strong px-3 text-xs text-white',
					canSave ? 'cursor-pointer hover:bg-ink' : 'cursor-not-allowed opacity-50',
				].join(' ')}
			>
				{isSaving ? 'Saving…' : 'Save'}
			</button>
		</div>
	);
}
